import type { MapControls, PerspectiveCamera } from "@react-three/drei";
import type { ElementRef } from "react";
import { useLayoutEffect, useMemo, useRef, useState } from "react";

import { CAMERA_VIEW } from "./camera.constants.js";
import type { CameraBounds, CameraViewport } from "./camera.types.js";
import {
  applyCameraView,
  getCameraDistanceLimits,
  getCameraViewConfig,
  getDefaultCameraPlacement,
  getEffectiveTargetBounds,
  hasPannableTargetBounds,
} from "./camera.utils.js";

interface UseOfficeCameraOptions {
  topDown: boolean;
}

export function useOfficeCamera({ topDown }: UseOfficeCameraOptions) {
  const [officeBounds, setOfficeBounds] = useState<CameraBounds | null>(null);
  const [viewport, setViewport] = useState<CameraViewport | null>(null);
  const viewConfig = useMemo(() => getCameraViewConfig(topDown), [topDown]);
  const cameraRef = useRef<ElementRef<typeof PerspectiveCamera> | null>(null);
  const orbitControlsRef = useRef<ElementRef<typeof MapControls>>(null);
  const defaultPlacement = useMemo(
    () =>
      officeBounds && viewport
        ? getDefaultCameraPlacement({
            officeBounds,
            viewConfig,
            viewport,
          })
        : null,
    [officeBounds, viewport, viewConfig]
  );
  const distanceLimits = useMemo(
    () =>
      defaultPlacement === null
        ? null
        : getCameraDistanceLimits({
            defaultDistance: defaultPlacement.defaultDistance,
            viewConfig,
          }),
    [defaultPlacement, viewConfig]
  );
  const effectiveTargetBounds = useMemo(
    () =>
      getEffectiveTargetBounds({
        officeBounds,
        placement: defaultPlacement,
      }),
    [defaultPlacement, officeBounds]
  );
  const canPan = useMemo(
    () => hasPannableTargetBounds(effectiveTargetBounds),
    [effectiveTargetBounds]
  );
  const isSceneReady = defaultPlacement !== null;

  useLayoutEffect(() => {
    if (
      defaultPlacement === null ||
      distanceLimits === null ||
      !(cameraRef.current && orbitControlsRef.current)
    ) {
      return;
    }

    const syncCameraView = () => {
      if (!(cameraRef.current && orbitControlsRef.current)) {
        return;
      }

      applyCameraView({
        camera: cameraRef.current,
        canPan,
        controls: orbitControlsRef.current,
        distanceLimits,
        placement: defaultPlacement,
        viewConfig,
      });
    };

    syncCameraView();

    // Re-apply on the next frame so controls settle into the new 2D/3D mode
    // without requiring a second toggle.
    const animationFrameId = requestAnimationFrame(syncCameraView);

    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [canPan, defaultPlacement, distanceLimits, viewConfig]);

  return {
    cameraFov: CAMERA_VIEW.fov,
    cameraNear: CAMERA_VIEW.near,
    cameraRef,
    canPan,
    distanceLimits,
    effectiveTargetBounds,
    initialCameraPosition: CAMERA_VIEW.initialPosition,
    isSceneReady,
    orbitControlsRef,
    setOfficeBounds,
    setViewport,
    viewConfig,
  };
}
